import { getAddress } from "ethers";
import { useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";

import { getServerUrl } from "config/backend";
import { ASSETCHAIN_TESTNET } from "config/chains";

import { PerformanceSnapshot, PriceSnapshot, buildPerformanceSnapshots, calculatePoolPerformance } from "./performance";
import { GlvInfoData, MarketsInfoData } from "./types";
import { Period } from "./usePoolsTimeRange";

type PriceResponse = {
  prices: PriceSnapshot[];
  aprSnapshots?: AprSnapshot[];
};

export type AprSnapshot = {
  address: string;
  aprByFee: string;
  aprByBorrowingFee: string;
  snapshotTimestamp: number;
};

export type PriceData = {
  [tokenAddress: string]: PriceSnapshot[];
};

export type AprData = {
  [marketAddress: string]: AprSnapshot[];
};

export type PriceDataMapped = {
  [tokenAddress: string]: Record<number, PriceSnapshot>;
};

type GmGlvPerformanceResponse = {
  pricesByToken: PriceData;
  aprSnapshotsByMarket: AprData;
};

function useIsDocumentVisible() {
  const [isVisible, setIsVisible] = useState(() => {
    if (typeof document === "undefined") return true;
    return !document.hidden;
  });

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const onVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  return isVisible;
}

export function useGmGlvPerformance({
  chainId,
  period,
  gmData,
  glvData,
  tokenAddresses,
  marketAddresses,
}: {
  chainId: number;
  period: Period;
  gmData: MarketsInfoData | undefined;
  glvData: GlvInfoData | undefined;
  tokenAddresses?: string[];
  marketAddresses?: string[];
}) {
  const url = getServerUrl(chainId, "/prices/snapshots");
  const isDocumentVisible = useIsDocumentVisible();
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestKeyRef = useRef<string | undefined>(undefined);

  const normalizedTokenAddresses = useMemo(() => {
    if (!tokenAddresses?.length) {
      return undefined;
    }

    return Array.from(new Set(tokenAddresses.map((a) => getAddress(a)))).sort();
  }, [tokenAddresses]);
  const normalizedMarketAddresses = useMemo(() => {
    if (!marketAddresses?.length) {
      return undefined;
    }

    return Array.from(new Set(marketAddresses.map((a) => getAddress(a)))).sort();
  }, [marketAddresses]);

  const shouldFetch = normalizedTokenAddresses && normalizedTokenAddresses.length > 0;
  const isTotalRange = period.periodStart === 0;
  const refreshInterval = isDocumentVisible ? (isTotalRange ? 60_000 : 15_000) : 0;

  const { data, isLoading, isValidating } = useSWR<GmGlvPerformanceResponse | undefined>(
    shouldFetch
      ? [
          "useGmGlvPerformance",
          chainId,
          period.periodStart,
          period.periodEnd,
          normalizedTokenAddresses!.join(","),
          normalizedMarketAddresses?.join(",") ?? "",
        ]
      : null,
    {
      keepPreviousData: true,
      revalidateOnFocus: false,
      refreshWhenHidden: false,
      refreshWhenOffline: false,
      refreshInterval,
      dedupingInterval: 15_000,
      shouldRetryOnError: (error) => error?.name !== "AbortError",
      fetcher: async () => {
        const queryParams = new URLSearchParams({
          fromTimestamp: period.periodStart.toString(),
          toTimestamp: period.periodEnd.toString(),
          tokenAddresses: normalizedTokenAddresses!.join(","),
          network: chainId === ASSETCHAIN_TESTNET ? "testnet" : "mainnet",
        });
        if (normalizedMarketAddresses && normalizedMarketAddresses.length > 0) {
          queryParams.set("marketAddresses", normalizedMarketAddresses.join(","));
        }

        const requestKey = queryParams.toString();
        if (requestKeyRef.current !== requestKey && abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        requestKeyRef.current = requestKey;

        const controller = new AbortController();
        abortControllerRef.current = controller;

        const res = await fetch(`${url}?${queryParams}`, { signal: controller.signal });
        if (!res.ok) {
          throw new Error(`Failed to fetch snapshots: ${res.status}`);
        }
        const result: PriceResponse = await res.json();

        const pricesByToken = result.prices.reduce(
          (acc, price) => {
            const tokenAddress = getAddress(price.token);
            if (!acc[tokenAddress]) {
              acc[tokenAddress] = [];
            }

            acc[tokenAddress].push({
              ...price,
              token: tokenAddress,
            });
            return acc;
          },
          {} as Record<string, PriceSnapshot[]>
        );

        const aprSnapshotsByMarket = (result.aprSnapshots ?? []).reduce(
          (acc, aprSnapshot) => {
            const marketAddress = getAddress(aprSnapshot.address);
            if (!acc[marketAddress]) {
              acc[marketAddress] = [];
            }
            acc[marketAddress].push({
              ...aprSnapshot,
              address: marketAddress,
            });
            return acc;
          },
          {} as Record<string, AprSnapshot[]>
        );

        return { pricesByToken, aprSnapshotsByMarket };
      },
    }
  );

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // ... (useGmGlvPerformance logic continued)
  const priceData = useMemo(() => {
    const rawPrices = data?.pricesByToken;
    if (!rawPrices) return {};

    return Object.entries(rawPrices).reduce(
      (acc, [token, prices]) => {
        const normalizedToken = getAddress(token);
        acc[normalizedToken] = prices.reduce(
          (acc, price) => {
            // if snapshot price is already in the acc and new price is onchainFeed, skip
            if (price.type === "onchainFeed" && acc[price.snapshotTimestamp]) {
              return acc;
            }

            acc[price.snapshotTimestamp] = price;

            return acc;
          },
          {} as Record<number, PriceSnapshot>
        );
        return acc;
      },
      {} as Record<string, Record<number, PriceSnapshot>>
    );
  }, [data]);

  const timestampsData = useMemo(() => {
    return Object.entries(priceData).reduce(
      (acc, [address, prices]) => {
        const normalizedAddress = getAddress(address);
        const timestamps = Object.keys(prices)
          .map(Number)
          .sort((a, b) => a - b);
        acc[normalizedAddress] = timestamps;
        return acc;
      },
      {} as Record<string, number[]>
    );
  }, [priceData]);

  const glvPerformanceSnapshots = useMemo(
    () => getPoolsPerformanceSnapshots(glvData, priceData, timestampsData),
    [priceData, glvData, timestampsData]
  );
  const gmPerformanceSnapshots = useMemo(
    () => getPoolsPerformanceSnapshots(gmData, priceData, timestampsData),
    [priceData, gmData, timestampsData]
  );

  const glvPerformance = useMemo(
    () => getPoolsPerformance(glvData, priceData, timestampsData),
    [priceData, glvData, timestampsData]
  );
  const gmPerformance = useMemo(
    () => getPoolsPerformance(gmData, priceData, timestampsData),
    [priceData, gmData, timestampsData]
  );

  return {
    glvPerformanceSnapshots,
    gmPerformanceSnapshots,
    glvPerformance,
    gmPerformance,
    prices: data?.pricesByToken,
    aprSnapshots: data?.aprSnapshotsByMarket,
    isLoading: shouldFetch && (isLoading || (isValidating && !data)),
  };
}

export type PerformanceSnapshotsData = {
  [address: string]: PerformanceSnapshot[];
};

const getPoolsPerformanceSnapshots = (
  poolsData:
    | Record<
        string,
        {
          longTokenAddress: string;
          shortTokenAddress: string;
        }
      >
    | undefined,
  priceData: PriceDataMapped,
  timestampsData: Record<string, number[]>
): PerformanceSnapshotsData => {
  if (!poolsData) {
    return {};
  }

  return Object.entries(poolsData).reduce(
    (acc, curr) => {
      const [address, pool] = curr;
      const normalizedAddress = getAddress(address);
      const longTokenAddress = getAddress(pool.longTokenAddress);
      const shortTokenAddress = getAddress(pool.shortTokenAddress);

      const performanceSnapshots = buildPerformanceSnapshots({
        longTokenPrices: priceData?.[longTokenAddress] || {},
        shortTokenPrices: priceData?.[shortTokenAddress] || {},
        poolPrices: priceData?.[normalizedAddress] || {},
        timestamps: timestampsData[normalizedAddress] || [],
      });
      acc[normalizedAddress] = performanceSnapshots;
      return acc;
    },
    {} as Record<string, PerformanceSnapshot[]>
  );
};

export type PerformanceData = {
  [address: string]: number;
};

const getPoolsPerformance = (
  poolsData:
    | Record<
        string,
        {
          longTokenAddress: string;
          shortTokenAddress: string;
        }
      >
    | undefined,
  priceData: PriceDataMapped,
  timestampsData: Record<string, number[]>
): PerformanceData => {
  if (!poolsData) {
    return {};
  }

  return Object.entries(poolsData).reduce((acc, curr) => {
    const [address, pool] = curr;
    const normalizedAddress = getAddress(address);
    const longTokenAddress = getAddress(pool.longTokenAddress);
    const shortTokenAddress = getAddress(pool.shortTokenAddress);

    const performance = calculatePoolPerformance({
      longTokenPrices: priceData[longTokenAddress] || {},
      shortTokenPrices: priceData[shortTokenAddress] || {},
      poolPrices: priceData[normalizedAddress] || {},
      timestamps: timestampsData[normalizedAddress] || [],
    });

    if (performance === undefined || Number.isNaN(performance) || !Number.isFinite(performance)) {
      return acc;
    }

    acc[normalizedAddress] = performance;
    return acc;
  }, {} as PerformanceData);
};
